import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// SERVICES
import { DebounceService } from 'src/app/core/services/debounce.service';
import { PaginationHelperService } from 'src/app/services/pagination-helper.service';
import { rdpdService } from '../rdpd.service';

// CONSTANTS
import { VIEW_RDPD_TABLE_SUB_HEADERS, VIEW_RDPD_TABLE_SUB_HEADERS_NON_SORTABLE} from 'src/app/lib/constants/rdpd.constants';

// VALUE OBJECTS
import * as vo from 'src/app/core/model/server/vo/_index';

export interface ViewDistributorsData {
  distributorType: 'regional' | 'provincial' | 'retailer';
  accountGuid: string;
}

@Component({
  selector: 'app-view-distributors',
  templateUrl: './view-distributors.component.html',
  styleUrl: './view-distributors.component.scss',
})
export class ViewDistributorsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  searchForm!: UntypedFormGroup;
  limitForm!: UntypedFormGroup;
  distributorType!: 'regional' | 'provincial' | 'retailer';

  // ─── Table ─────────────────────────────────────────────────────────────────

  tableSubHeader = VIEW_RDPD_TABLE_SUB_HEADERS;
  nonSortableSubHeader = VIEW_RDPD_TABLE_SUB_HEADERS_NON_SORTABLE;
  // ─── Data ──────────────────────────────────────────────────────────────────

  isLoading = false;

  /** retailer → single PD wrapped in array */
  retailerPdList:  vo.DistributorInfo[] = [];

  /** regional → paginated PD list */
  regionalPdList:  vo.DistributorInfo[] = [];
  regionalPd_total       = 0;
  regionalPd_totalPages  = 0;
  regionalPd_currentPage = 1;

  /** provincial → single RD wrapped in array */
  provincialRdList: vo.DistributorInfo[] = [];

  /** provincial → paginated retailers */
  provincialRtList:  vo.DistributorInfo[] = [];
  provincialRt_total       = 0;
  provincialRt_totalPages  = 0;
  provincialRt_currentPage = 1;

  // ─── Search & Limit ────────────────────────────────────────────────────────

  search_keyword = '';
  searchKey: any = null;
  limitKey: any  = null;
  size           = 10;

  // ─── Sorting ───────────────────────────────────────────────────────────────

  sort_column               = 'assignedDate';
  sort_type: 'ASC' | 'DESC' = 'ASC';

  // ─── Constructor ───────────────────────────────────────────────────────────

  constructor(
    public dialogRef: MatDialogRef<ViewDistributorsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ViewDistributorsData,
    private fb: UntypedFormBuilder,
    private toastr: ToastrService,
    public debounce: DebounceService,
    public paginationHelper: PaginationHelperService,
    private rdpdFacade: rdpdService,
  ) {}

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.distributorType = this.data.distributorType;
    this.initializeForm();
    this.loadChain();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Form Initialization ───────────────────────────────────────────────────

  initializeForm(): void {
    this.searchForm = this.fb.group({ search: [''] });
    this.limitForm  = this.fb.group({ limit: [this.size] });

    this.searchKey = this.debounce.debounce(() => {
      this.search_keyword = this.searchForm.value.search;
      this.regionalPd_currentPage   = 1;
      this.provincialRt_currentPage = 1;
      this.loadChain(1);
    }, 500);

    this.limitKey = this.debounce.debounce(() => {
      this.regionalPd_currentPage   = 1;
      this.provincialRt_currentPage = 1;
      this.loadChain(1);
    }, 500);
  }

  // ─── Load Chain ────────────────────────────────────────────────────────────

  private buildRequest(): vo.GetDistributorsChainRequest {
    return {
      accountGuid: this.data.accountGuid,
      search:      this.search_keyword || null,
      sortBy:      { [this.sort_column]: this.sort_type },
    };
  }

  loadChain(page: number = 1): void {
    this.isLoading = true;

    this.rdpdFacade
      .getDistributorsChain(page, this.size, this.buildRequest())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next:  (res) => this.handleChainResponse(res),
        error: (err) => {
          this.toastr.error('Failed to fetch distribution chain.');
          this.isLoading = false;
        },
      });
  }

  // ─── Response Handler ──────────────────────────────────────────────────────

  private handleChainResponse(res: vo.GetDistributionChainResponse): void {
    if (res?.result) {
      const r = res.result;

      // retailer → single provincialDistributor
      if (this.distributorType === 'retailer') {
        this.retailerPdList = r.provincialDistributor ? [r.provincialDistributor] : [];
      }

      // regional → paginated provincialDistributors
      if (this.distributorType === 'regional') {
        const pd = r.provincialDistributors;
        this.regionalPdList        = pd?.data         ?? [];
        this.regionalPd_total      = pd?.totalItems   ?? 0;
        this.regionalPd_totalPages = pd?.totalPages   ?? 0;
        this.regionalPd_currentPage = pd?.currentPage ?? 1;
      }

      // provincial → single regionalDistributor + paginated retailers
      if (this.distributorType === 'provincial') {
        this.provincialRdList = r.regionalDistributor ? [r.regionalDistributor] : [];

        const rt = r.retailers;
        this.provincialRtList        = rt?.data         ?? [];
        this.provincialRt_total      = rt?.totalItems   ?? 0;
        this.provincialRt_totalPages = rt?.totalPages   ?? 0;
        this.provincialRt_currentPage = rt?.currentPage ?? 1;
      }
    }

    this.isLoading = false;
  }

  // ─── Table Controls ────────────────────────────────────────────────────────

  onLimitChangeFromComponent(event: any): void {
    this.size = event.target.value;
    this.limitForm.patchValue({ limit: this.size });
    this.regionalPd_currentPage   = 1;
    this.provincialRt_currentPage = 1;
    this.loadChain(1);
  }

  onSearchKeyupFromComponent(event: any): void {
    this.searchForm.patchValue({ search: event.target.value });
    this.searchKey();
  }

  keyDownFunction(event: KeyboardEvent): void {
    if (event.keyCode === 13) { this.searchList(); }
  }

  searchList(): void {
    this.search_keyword = this.searchForm.value.search;
    this.regionalPd_currentPage   = 1;
    this.provincialRt_currentPage = 1;
    this.loadChain(1);
  }

  // ─── Sorting ───────────────────────────────────────────────────────────────

  sortList(sort_column: string, sort_type: 'ASC' | 'DESC'): void {
    const { column, type } = this.paginationHelper.toggleSort(
      this.sort_column, this.sort_type, sort_column, sort_type,
    );
    this.sort_column = column;
    this.sort_type   = type;
    this.regionalPd_currentPage   = 1;
    this.provincialRt_currentPage = 1;
    this.loadChain(1);
  }

  // ─── Pagination (regional PD) ──────────────────────────────────────────────

  setRegionalPdPage(num: number): void {
    this.regionalPd_currentPage = num;
    this.loadChain(num);
  }

  nextRegionalPdPage(): void {
    if (this.regionalPd_currentPage < this.regionalPd_totalPages) {
      this.regionalPd_currentPage++;
      this.loadChain(this.regionalPd_currentPage);
    }
  }

  prevRegionalPdPage(): void {
    if (this.regionalPd_currentPage > 1) {
      this.regionalPd_currentPage--;
      this.loadChain(this.regionalPd_currentPage);
    }
  }

  // ─── Pagination (provincial retailers) ────────────────────────────────────

  setProvincialRtPage(num: number): void {
    this.provincialRt_currentPage = num;
    this.loadChain(num);
  }

  nextProvincialRtPage(): void {
    if (this.provincialRt_currentPage < this.provincialRt_totalPages) {
      this.provincialRt_currentPage++;
      this.loadChain(this.provincialRt_currentPage);
    }
  }

  prevProvincialRtPage(): void {
    if (this.provincialRt_currentPage > 1) {
      this.provincialRt_currentPage--;
      this.loadChain(this.provincialRt_currentPage);
    }
  }

  // ─── Refresh ───────────────────────────────────────────────────────────────

  refresh(): void {
    this.search_keyword = '';
    this.size  = 10;
    this.searchForm.reset({ search: '' });
    this.sort_column              = 'assignedDate';
    this.sort_type                = 'ASC';
    this.regionalPd_currentPage   = 1;
    this.provincialRt_currentPage = 1;
    this.loadChain(1);
  }

  // ─── Close ─────────────────────────────────────────────────────────────────

  closeDialog(): void {
    this.dialogRef.close();
  }
}
